// ═══════════════════════════════════════════════════════════════════════════
// useCategorias — Hook + helpers ABM de Categorías por empresa
// ═══════════════════════════════════════════════════════════════════════════
//
// Cada empresa define sus propias categorías de productos (rubros del negocio).
// Las categorías son AGNÓSTICAS de plataforma — el mismo producto se vende en
// MLA, TN, Tienda Propia y Amazon con la misma categoría del seller.
//
// Las categorías llevan el `ivaPct` que se hereda al producto. El producto
// puede overridear con su propio `ivaPct` (cascada en el dashboard).
//
// Seed: `seedCategoriasDesdeProductos(empresaId)` toma los strings únicos
// del campo legacy `productos.categoria`, crea docs en `categorias/{id}` con
// `ivaPct: 0.21` por default, y linkea cada producto seteando `categoriaId`.
// El campo legacy NO se borra — queda como histórico.
// ═══════════════════════════════════════════════════════════════════════════

import { useMemo } from 'react'
import {
  collection, query, where, doc, addDoc, updateDoc, deleteDoc,
  getDocs, serverTimestamp, writeBatch,
} from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { COLLECTIONS } from '@core/lib/constants'
import { useDashboardData } from '@core/hooks/useDashboardData'
import { SIN_CAP_DECLARADO, MOTIVO_CATALOGO_DEL_TENANT } from '@core/lib/cotaDeLectura'

// ─── Hook: la lectura la hace la plataforma, el orden lo hace el vertical ──
//
// ⭐ POR QUÉ ESTE HOOK YA NO ABRE SU PROPIO `onSnapshot` (17-ago-2026, ítem 18)
// ────────────────────────────────────────────────────────────────────────────
// Un hook de vertical que lee Firestore por su cuenta es la vía barata para una
// lectura sin cota: el candado del ítem 18 vive en `useDashboardData`, así que
// todo lo que no pasa por ahí queda afuera de la regla. Esta es la "segunda
// puerta" que el ítem declara cerrar. El hook se CONSERVA —sus cuatro
// consumidores no cambian una línea— pero adentro delega la lectura.
//
// ⭐⭐ Y EL ORDEN SE QUEDA ACÁ, CLIENT-SIDE. No es una comodidad:
//   · Ordena por DOS claves (`orden` asc y, en empate, `nombre`), y el hook
//     horizontal acepta UN solo campo.
//   · Los tres escritores guardan `orden: null` explícito, así que hoy todas
//     empatan: bajo un `orderBy('orden')` de Firestore el desempate lo haría el
//     nombre del DOCUMENTO, que es el orden sin significado que el ítem 18 vetó.
//   · `localeCompare(es)` no es el orden de Firestore ('Ñandú' va entre 'N' y
//     'O' en español, y después de 'Z' por bytes).
//   · Y un `orderBy` de Firestore EXCLUYE los docs sin el campo.
// La red que lo blinda es `useCategorias.test.jsx` (#923), con eñes y acentos
// en el fixture a propósito.

export function useCategorias(empresaId) {
  const { data: docs, loading, error } = useDashboardData({
    collectionName: COLLECTIONS.CATEGORIAS,
    empresaId,
    // Catálogo del tenant: lo carga una persona. Un umbral acá no sonaría nunca.
    cap: SIN_CAP_DECLARADO,
    motivoSinCap: MOTIVO_CATALOGO_DEL_TENANT,
    enabled: !!empresaId,
  })

  // El sort ya tiene que copiar el array, así que el `_docId` que agrega el hook
  // horizontal se descarta en la misma pasada: el contrato que ven los cuatro
  // consumidores queda idéntico al de antes de la migración.
  const data = useMemo(() => (
    docs
      .map(({ _docId, ...resto }) => resto)
      .sort((a, b) => {
        const oa = a.orden ?? 999
        const ob = b.orden ?? 999
        if (oa !== ob) return oa - ob
        return (a.nombre || '').localeCompare(b.nombre || '', 'es')
      })
  ), [docs])

  return { data, loading, error }
}

// ─── ABM ─────────────────────────────────────────────────────────────────

export async function crearCategoria(empresaId, payload, userId) {
  const ahora = serverTimestamp()
  const ref = await addDoc(collection(db, COLLECTIONS.CATEGORIAS), {
    empresaId,
    nombre:      payload.nombre?.trim() || '',
    descripcion: payload.descripcion?.trim() || null,
    ivaPct:      Number(payload.ivaPct) || 0.21,
    ivaSource:   payload.ivaSource || 'manual',
    padreId:     payload.padreId || null,
    orden:       payload.orden ?? null,
    activa:      payload.activa !== false,
    rentabilidadMinima: payload.rentabilidadMinima ?? null, // corte de la categoría (fracción) — ADR 032
    creadoPor:   userId || null,
    creadoEn:    ahora,
    actualizadoEn: ahora,
  })
  return ref.id
}

export async function actualizarCategoria(categoriaId, payload, userId) {
  const ref = doc(db, COLLECTIONS.CATEGORIAS, categoriaId)
  const updates = {
    actualizadoPor: userId || null,
    actualizadoEn:  serverTimestamp(),
  }
  if (payload.nombre !== undefined)      updates.nombre      = payload.nombre?.trim() || ''
  if (payload.descripcion !== undefined) updates.descripcion = payload.descripcion?.trim() || null
  if (payload.ivaPct !== undefined)      updates.ivaPct      = Number(payload.ivaPct)
  if (payload.ivaSource !== undefined)   updates.ivaSource   = payload.ivaSource
  if (payload.padreId !== undefined)     updates.padreId     = payload.padreId || null
  if (payload.orden !== undefined)       updates.orden       = payload.orden
  if (payload.activa !== undefined)      updates.activa      = payload.activa
  if (payload.rentabilidadMinima !== undefined) updates.rentabilidadMinima = payload.rentabilidadMinima ?? null
  await updateDoc(ref, updates)
}

export async function eliminarCategoria(categoriaId, empresaId) {
  // Validación: no permitir borrar si tiene productos asociados.
  const productosQ = query(
    collection(db, COLLECTIONS.PRODUCTOS),
    where('empresaId', '==', empresaId),
    where('categoriaId', '==', categoriaId),
  )
  const snap = await getDocs(productosQ)
  if (!snap.empty) {
    throw new Error(`La categoría tiene ${snap.size} producto(s) asociado(s). Reasigná los productos primero.`)
  }
  await deleteDoc(doc(db, COLLECTIONS.CATEGORIAS, categoriaId))
}

// ─── Seed: importar categorías de productos existentes ──────────────────
// Toma todos los productos de la empresa que tienen `categoria` (string legacy)
// y NO tienen `categoriaId` (FK nueva), agrupa por nombre único, crea un doc
// en `categorias` para cada uno con ivaPct: 0.21 default, y luego setea el
// `categoriaId` correspondiente en cada producto.
//
// Idempotente: si una categoría con ese nombre ya existe (case-insensitive),
// no la duplica — reusa la existente y linkea los productos huérfanos.

export async function seedCategoriasDesdeProductos(empresaId, userId) {
  if (!empresaId) throw new Error('empresaId requerido')

  // 1) Traer todos los productos de la empresa
  const productosQ = query(
    collection(db, COLLECTIONS.PRODUCTOS),
    where('empresaId', '==', empresaId),
  )
  const productosSnap = await getDocs(productosQ)

  // 2) Recolectar strings únicos de `categoria` legacy de productos sin categoriaId
  const stringsPorClave = new Map()  // key normalizada → nombre original
  const productosPorClave = new Map() // key normalizada → array de productoId
  for (const d of productosSnap.docs) {
    const data = d.data()
    if (data.categoriaId) continue   // ya migrado, saltar
    const cat = (data.categoria || '').trim()
    if (!cat) continue
    const key = cat.toLowerCase()
    if (!stringsPorClave.has(key)) {
      stringsPorClave.set(key, cat)
      productosPorClave.set(key, [])
    }
    productosPorClave.get(key).push(d.id)
  }

  if (stringsPorClave.size === 0) {
    return { ok: true, creadas: 0, productosLinkeados: 0, msg: 'No hay categorías legacy para migrar' }
  }

  // 3) Traer categorías existentes para evitar duplicados por nombre
  const catsQ = query(
    collection(db, COLLECTIONS.CATEGORIAS),
    where('empresaId', '==', empresaId),
  )
  const catsSnap = await getDocs(catsQ)
  const existentesPorNombre = new Map()
  for (const d of catsSnap.docs) {
    const data = d.data()
    existentesPorNombre.set((data.nombre || '').toLowerCase(), d.id)
  }

  // 4) Crear las que faltan + recolectar el mapeo nombre → categoriaId
  const ahora = serverTimestamp()
  const mapaCatPorClave = new Map()  // key → categoriaId
  let creadas = 0
  for (const [key, nombreOriginal] of stringsPorClave.entries()) {
    if (existentesPorNombre.has(key)) {
      mapaCatPorClave.set(key, existentesPorNombre.get(key))
    } else {
      const ref = await addDoc(collection(db, COLLECTIONS.CATEGORIAS), {
        empresaId,
        nombre:        nombreOriginal,
        descripcion:   null,
        ivaPct:        0.21,
        ivaSource:     'default-seed',
        padreId:       null,
        orden:         null,
        activa:        true,
        creadoPor:     userId || 'seed',
        creadoEn:      ahora,
        actualizadoEn: ahora,
      })
      mapaCatPorClave.set(key, ref.id)
      creadas++
    }
  }

  // 5) Linkear los productos en lotes de 400 (límite de batch)
  let productosLinkeados = 0
  let batch = writeBatch(db)
  let inBatch = 0
  for (const [key, productoIds] of productosPorClave.entries()) {
    const categoriaId = mapaCatPorClave.get(key)
    if (!categoriaId) continue
    for (const pid of productoIds) {
      const ref = doc(db, COLLECTIONS.PRODUCTOS, pid)
      batch.update(ref, {
        categoriaId,
        actualizadoEn: ahora,
      })
      inBatch++
      productosLinkeados++
      if (inBatch >= 400) {
        await batch.commit()
        batch = writeBatch(db)
        inBatch = 0
      }
    }
  }
  if (inBatch > 0) await batch.commit()

  return {
    ok: true,
    creadas,
    productosLinkeados,
    yaExistentes: stringsPorClave.size - creadas,
    msg: `Migración OK: ${creadas} categorías creadas, ${productosLinkeados} productos linkeados, ${stringsPorClave.size - creadas} categorías ya existían`,
  }
}

// ─── Resolver IVA con cascada ───────────────────────────────────────────
// Cascada: producto.ivaPct → categoria.ivaPct → empresa default → plataforma 21%
// Devuelve { ivaPct, source } donde source explica de dónde salió.

export function resolverIvaPct({ producto, categoria, empresaImpuestos, defaultPlataforma = 0.21 }) {
  if (producto?.ivaPct != null) {
    return { ivaPct: producto.ivaPct, source: 'producto' }
  }
  if (categoria?.ivaPct != null) {
    return { ivaPct: categoria.ivaPct, source: 'categoria', categoriaNombre: categoria.nombre }
  }
  if (empresaImpuestos?.ivaGeneral != null) {
    return { ivaPct: empresaImpuestos.ivaGeneral, source: 'empresa' }
  }
  return { ivaPct: defaultPlataforma, source: 'plataforma-default' }
}
